import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, Mail, Phone, MapPin, Clock, ArrowRight } from "lucide-react";
import axios from "axios";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Studio de jardin / pièce en plus",
  "Chambre d'amis / hébergement familial",
  "Location saisonnière (Airbnb, gîte...)",
  "Résidence secondaire / pied-à-terre",
  "Projet en cours de réflexion",
  "Autre",
];

const STEPS = [
  "Échange téléphonique où visio pour cadrer votre projet",
  "Visite de notre showroom si souhaité",
  "Proposition technique et commerciale adaptée",
  "Accompagnement tout au long du projet",
];

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const productInterest = searchParams.get("product") || "";
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", project_type: "", message: productInterest ? `Je souhaité en savoir plus sur le ${productInterest}.` : "",
  });

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Veuillez remplir les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, product_interest: productInterest });
      setSent(true);
      toast.success("Message envoyé avec succes.");
    } catch {
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="contact-success" className="pt-20 min-h-screen flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-natural-wood">Merci</span>
          <h2 className="font-heading text-3xl tracking-tight text-soft-black mt-4 mb-4">Message envoyé</h2>
          <p className="font-body text-soft-black/60 leading-relaxed">
            Nos équipés s'engagent à vous répondre sous 48 heures ouvrées.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className="pt-20 min-h-screen">
      {/* Hero */}
      <section className="py-10 md:py-14 px-6 md:px-12 lg:px-24 border-b border-warm-grey/20">
        <div className="max-w-7xl mx-auto">
          <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">Contact</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-soft-black mt-4 mb-4">
            Presentez-nous votre projet
          </h1>
          <p className="font-body text-base md:text-lg text-soft-black/60 leading-relaxed max-w-2xl">Parlez-nous de votre projet</p>
        </div>
      </section>

      {/* Main content */}
      <section className="py-10 md:py-14 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          {/* Intro text */}
          <p className="font-body text-soft-black/70 leading-relaxed max-w-3xl mb-16">
            Vous avez un projet d'hébergement, d'accueil où d'habitat ? Nos équipés sont à votre écoute
            pour comprendre vos besoins et vous accompagner vers la solution la plus adaptée.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20">
            {/* Form — Left */}
            <div className="lg:col-span-7">
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div>
                    <Label htmlFor="name" className="font-mono text-xs uppercase tracking-[0.15em] text-soft-black/60">
                      Nom et prenom *
                    </Label>
                    <Input id="name" name="name" data-testid="contact-name" value={form.name} onChange={handleChange}
                      className="mt-2 border-0 border-b border-soft-black/20 rounded-none bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-soft-black font-body" />
                  </div>
                  <div>
                    <Label htmlFor="company" className="font-mono text-xs uppercase tracking-[0.15em] text-soft-black/60">
                      Société / Organisation
                    </Label>
                    <Input id="company" name="company" data-testid="contact-company" value={form.company} onChange={handleChange}
                      className="mt-2 border-0 border-b border-soft-black/20 rounded-none bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-soft-black font-body" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div>
                    <Label htmlFor="email" className="font-mono text-xs uppercase tracking-[0.15em] text-soft-black/60">
                      Email *
                    </Label>
                    <Input id="email" name="email" type="email" data-testid="contact-email" value={form.email} onChange={handleChange}
                      className="mt-2 border-0 border-b border-soft-black/20 rounded-none bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-soft-black font-body" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="font-mono text-xs uppercase tracking-[0.15em] text-soft-black/60">
                      Téléphone
                    </Label>
                    <Input id="phone" name="phone" data-testid="contact-phone" value={form.phone} onChange={handleChange}
                      className="mt-2 border-0 border-b border-soft-black/20 rounded-none bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-soft-black font-body" />
                  </div>
                </div>

                <div>
                  <Label className="font-mono text-xs uppercase tracking-[0.15em] text-soft-black/60">
                    Type de projet
                  </Label>
                  <Select value={form.project_type} onValueChange={(val) => setForm((f) => ({ ...f, project_type: val }))}>
                    <SelectTrigger data-testid="contact-project-type" className="mt-2 border-0 border-b border-soft-black/20 rounded-none bg-transparent px-0 py-3 focus:ring-0 font-body text-sm h-auto shadow-none">
                      <SelectValue placeholder="Sélectionnez le type de projet" />
                    </SelectTrigger>
                    <SelectContent className="bg-off-white border border-warm-grey/30 rounded-none shadow-none">
                      {PROJECT_TYPES.map((t) => (
                        <SelectItem key={t} value={t} className="font-body text-sm cursor-pointer rounded-none hover:bg-light-beige focus:bg-light-beige">
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message" className="font-mono text-xs uppercase tracking-[0.15em] text-soft-black/60">
                    Message *
                  </Label>
                  <textarea id="message" name="message" data-testid="contact-message" value={form.message} onChange={handleChange}
                    rows={5}
                    className="mt-2 w-full border-0 border-b border-soft-black/20 rounded-none bg-transparent px-0 py-3 focus-visible:outline-none focus-visible:border-soft-black font-body text-sm resize-none" />
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4">
                  <button type="submit" data-testid="contact-submit-btn" disabled={loading}
                    className="flex items-center gap-2 bg-soft-black text-off-white px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-natural-wood transition-colors duration-300 disabled:opacity-50">
                    {loading ? "Envoi..." : "Envoyer le message"} <Send className="h-3.5 w-3.5" />
                  </button>
                  <span className="font-mono text-xs text-soft-black/40">* Champs obligatoires</span>
                </div>
              </form>
            </div>

            {/* Sidebar — Right */}
            <div className="lg:col-span-5 space-y-12">
              {/* Coordonnées */}
              <div>
                <h3 className="font-heading text-lg font-medium tracking-tight text-soft-black mb-6">Coordonnées</h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <Mail className="h-4 w-4 text-natural-wood mt-1 shrink-0" strokeWidth={1.5} />
                    <div>
                      <span className="font-mono text-xs uppercase tracking-[0.15em] text-soft-black/50 block mb-1">Email</span>
                      <span className="font-body text-sm text-soft-black">contact@yono-studio.com</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Phone className="h-4 w-4 text-natural-wood mt-1 shrink-0" strokeWidth={1.5} />
                    <div>
                      <span className="font-mono text-xs uppercase tracking-[0.15em] text-soft-black/50 block mb-1">Téléphone</span>
                      <span className="font-body text-sm text-soft-black">+33 (0)6 83 58 18 04</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <MapPin className="h-4 w-4 text-natural-wood mt-1 shrink-0" strokeWidth={1.5} />
                    <div>
                      <span className="font-mono text-xs uppercase tracking-[0.15em] text-soft-black/50 block mb-1">Adresse</span>
                      <span className="font-body text-sm text-soft-black">9 rue Pasteur<br />44310 Saint-Philbert-de-Grand-Lieu<br />France</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Délais */}
              <div className="border-t border-warm-grey/30 pt-8">
                <h3 className="font-heading text-lg font-medium tracking-tight text-soft-black mb-4">Délais de réponse</h3>
                <p className="font-body text-sm text-soft-black/60 leading-relaxed">
                  Nos équipés s'engagent à vous répondre sous 48 heures ouvrées.
                  Pour les demandès urgentes, n'hesitez pas à nous appeler directement.
                </p>
              </div>

              {/* Prochaines étapes */}
              <div className="border-t border-warm-grey/30 pt-8">
                <h3 className="font-heading text-lg font-medium tracking-tight text-soft-black mb-6">Prochaines étapes</h3>
                <div className="space-y-5">
                  {STEPS.map((step, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-7 h-7 border border-warm-grey/50 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="font-mono text-xs text-soft-black/50">{i + 1}</span>
                      </div>
                      <p className="font-body text-sm text-soft-black/70 leading-relaxed">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nous rencontrer */}
      <section className="py-10 md:py-14 px-6 md:px-12 lg:px-24 bg-light-beige">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-soft-black mb-4">Nous rencontrer</h2>
            <p className="font-body text-soft-black/60 leading-relaxed mb-6">
              Prenez rendez-vous pour visiter notre showroom et découvrir nos modules YONO.
            </p>
            <div className="flex items-start gap-3 mb-8">
              <MapPin className="h-4 w-4 text-natural-wood mt-1 shrink-0" strokeWidth={1.5} />
              <span className="font-body text-sm text-soft-black/70">9 rue Pasteur, 44310 Saint-Philbert-de-Grand-Lieu</span>
            </div>
            <a href="mailto:contact@yono-studio.com" data-testid="contact-rdv-btn"
              className="inline-flex items-center gap-2 bg-soft-black text-off-white px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-natural-wood transition-colors duration-300">
              Prendre rendez-vous <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
